// Lo que PISA o BORRA en la tienda del comerciante — fase 3 de
// ADR-2026-09-18-tiendanube-en-el-chat.
//
// Las dos capacidades de acá se distinguen del resto en una cosa: **no se deshacen desde
// Atlas**. Reemplazar borra fotos que sacó el comerciante; cambiar el precio toca lo que
// sus clientes ven ahora mismo.
//
// Por eso ninguna se ejecuta en el mismo turno en que se pide. La confirmación **no es
// lo que afirma el modelo**: es una marca que dejamos nosotros al avisar, y que sólo
// habilita la acción si la persona habló DESPUÉS. Es el mecanismo de `correr_flujo`
// (ADR-2026-09-17-flujos-el-permiso-para-gastar-no-lo-declara-el-modelo), que se ganó
// en una review donde pasar el costo exacto no alcanzó: el modelo puede encadenar
// "te aviso" y "confirmado" en la misma respuesta sin que nadie haya dicho nada.
import type { HerramientaChat } from './tipos';
import { elegirProductoDeAtlas, mensajeDeVarios, type ProductoDeAtlas } from './tiendanubeEnElChat';
import { anotarAviso, consumirAviso, puedeActuar } from './confirmacionVerificable';
import type { TnProductDetails } from '../lib/tiendaNube';

export const NOMBRE_REEMPLAZAR_FOTOS = 'reemplazar_fotos_del_producto';
export const NOMBRE_CAMBIAR_DATOS = 'cambiar_datos_en_la_tienda';

export const HERRAMIENTA_REEMPLAZAR_FOTOS: HerramientaChat = {
  name: NOMBRE_REEMPLAZAR_FOTOS,
  description:
    'Deja en la galería de un producto SÓLO las fotos que le pases: borra de la tienda todas las que tenga hoy, incluidas las que sacó el comerciante. NO SE DESHACE desde Atlas. Usala únicamente si pidió reemplazar, cambiar todas o dejar sólo estas; si dijo "agregá" o "subí", usá subir_fotos_a_la_tienda. La primera vez te va a devolver el aviso y no va a hacer nada: contale con todas las letras qué se borra y esperá su respuesta. Recién en tu turno siguiente, y sólo si dijo que sí, volvé a llamarla con confirmado.',
  input_schema: {
    type: 'object',
    properties: {
      producto: { type: 'string', description: 'El producto de Atlas, por nombre o por id.' },
      imagenes: {
        type: 'array',
        description: 'Los image_id de las fotos que quedan, en orden.',
        items: { type: 'string' },
      },
      confirmado: {
        type: 'boolean',
        description: 'Sólo true después de que el usuario dijo que sí, en un mensaje posterior al aviso.',
      },
    },
    required: ['producto', 'imagenes'],
  },
};

export const HERRAMIENTA_CAMBIAR_DATOS: HerramientaChat = {
  name: NOMBRE_CAMBIAR_DATOS,
  description:
    'Cambia el precio, el stock, el SKU, el nombre o la descripción de un producto EN LA TIENDA del usuario. Lo ven sus clientes al instante y no se deshace desde Atlas. Mandá sólo los campos que quiere cambiar. La primera vez te devuelve qué dice hoy y qué quedaría, sin tocar nada: mostráselo y esperá. Recién en tu turno siguiente, y sólo si confirmó, volvé a llamarla con confirmado.',
  input_schema: {
    type: 'object',
    properties: {
      producto: { type: 'string', description: 'El producto de Atlas, por nombre o por id.' },
      precio: { type: 'string', description: 'El precio nuevo, sólo el número.' },
      stock: { type: 'string', description: 'El stock nuevo.' },
      sku: { type: 'string', description: 'El SKU nuevo.' },
      nombre: { type: 'string', description: 'El nombre nuevo en la tienda.' },
      descripcion: { type: 'string', description: 'La descripción nueva.' },
      confirmado: {
        type: 'boolean',
        description: 'Sólo true después de que el usuario dijo que sí, en un mensaje posterior al aviso.',
      },
    },
    required: ['producto'],
  },
};

const normalizarLista = (ids: string[]): string => [...ids].sort().join('|');

// ── Reemplazar las fotos ────────────────────────────────────────────────────

export interface DependenciasDeReemplazar {
  productosDeAtlas: () => Promise<ProductoDeAtlas[]>;
  /** Manda el reemplazo. Devuelve cuántas fotos salieron. */
  reemplazar: (args: {
    productoId: string;
    tnProductId: number;
    imagenIds: string[];
  }) => Promise<{ imagenes: number }>;
}

export async function resolverReemplazarFotos(
  input: unknown,
  deps: DependenciasDeReemplazar,
  ctx: { turno: number | null; ultimoMensaje?: string },
): Promise<{ __texto: string }> {
  const texto = (t: string) => ({ __texto: t });
  const pedido = (input ?? {}) as { producto?: unknown; imagenes?: unknown; confirmado?: unknown };

  const nombre = typeof pedido.producto === 'string' ? pedido.producto.trim() : '';
  const imagenIds = Array.isArray(pedido.imagenes)
    ? pedido.imagenes.filter((i): i is string => typeof i === 'string' && i.trim().length > 0)
    : [];
  if (!nombre) return texto('Falta decir a qué producto. Preguntale cuál.');
  if (imagenIds.length === 0) {
    return texto(
      'Falta decir qué fotos quedan. Ojo: esto BORRA las demás, así que preguntá cuáles antes de seguir.',
    );
  }

  const eleccion = elegirProductoDeAtlas(nombre, await deps.productosDeAtlas());
  if (eleccion.tipo === 'varios') {
    return texto(mensajeDeVarios(nombre, eleccion.opciones));
  }
  if (eleccion.tipo === 'ninguno') {
    return texto(`No encontré "${nombre}" entre los productos de Atlas. Preguntale a cuál se refiere.`);
  }
  const producto = eleccion.producto;
  if (producto.tnProductId === null) {
    return texto(
      `"${producto.nombre}" no está vinculado a ningún producto de la tienda, así que no sé dónde reemplazar. Primero subí una foto con subir_fotos_a_la_tienda, que resuelve el vínculo preguntándole cuál es.`,
    );
  }

  const clave = `${producto.id}:${producto.tnProductId}:${normalizarLista(imagenIds)}`;
  const confirmado = pedido.confirmado === true;
  if (
    !puedeActuar({
      accion: NOMBRE_REEMPLAZAR_FOTOS,
      clave,
      confirmado,
      turno: ctx.turno,
      ultimoMensaje: ctx.ultimoMensaje,
    })
  ) {
    anotarAviso(NOMBRE_REEMPLAZAR_FOTOS, clave, ctx.turno);
    const cuantas = imagenIds.length === 1 ? 'esta foto' : `estas ${imagenIds.length} fotos`;
    return texto(
      `NO TOQUÉ NADA. Esto va a BORRAR de la tienda todas las fotos que "${producto.nombre}" tiene hoy —incluidas las que sacó él— y dejar sólo ${cuantas}. No se puede deshacer desde Atlas: recuperarlas sería volver a subirlas a mano en Tiendanube. Decíselo con esas palabras y preguntale si está seguro. Si en su próximo mensaje dice que sí, volvé a llamarme con confirmado en true.`,
    );
  }

  const { imagenes } = await deps.reemplazar({
    productoId: producto.id,
    tnProductId: producto.tnProductId,
    imagenIds,
  });
  consumirAviso(NOMBRE_REEMPLAZAR_FOTOS);
  if (imagenes === 0) {
    return texto('No se pudo mandar el reemplazo. Decíselo sin inventar la causa; no se borró nada.');
  }
  return texto(
    `Mandé el reemplazo de la galería de "${producto.nombre}": quedan ${imagenes === 1 ? '1 foto' : `${imagenes} fotos`}. Tarda un rato: decí que lo estás haciendo, nunca que ya está.`,
  );
}

// ── Cambiar datos del producto en la tienda ─────────────────────────────────

/** Los campos que se pueden tocar. Vacío = no pidió ningún cambio. */
export interface CambiosPedidos {
  precio?: string;
  stock?: string;
  sku?: string;
  nombre?: string;
  descripcion?: string;
}

const CAMPOS = ['precio', 'stock', 'sku', 'nombre', 'descripcion'] as const;

/** Lo que el modelo pidió cambiar, limpio y sin los campos que no mandó. */
export function cambiosPedidos(input: unknown): CambiosPedidos {
  const crudo = (input ?? {}) as Record<string, unknown>;
  const out: CambiosPedidos = {};
  for (const campo of CAMPOS) {
    const v = crudo[campo];
    if (typeof v === 'string' && v.trim().length > 0) out[campo] = v.trim();
  }
  return out;
}

/**
 * Los campos que en Tiendanube viven en la VARIANTE y no en el producto. Con una sola
 * variante da igual —se edita esa—, pero con varias no se sabe cuál tocar: el precio de
 * la talla M no es el de la L. Ahí no se adivina.
 */
export const CAMPOS_DE_VARIANTE = ['precio', 'stock', 'sku'] as const;

/** Qué cambia de verdad: lo pedido que difiere de lo que hay hoy. */
export function cambiosQueAplican(
  pedidos: CambiosPedidos,
  actual: TnProductDetails,
): { campo: string; de: string; a: string }[] {
  const hoy: Record<string, string> = {
    precio: actual.precio ?? '',
    stock: actual.stock != null ? String(actual.stock) : '',
    sku: actual.sku ?? '',
    nombre: actual.nombre ?? '',
    descripcion: actual.descripcion ?? '',
  };
  return CAMPOS.filter((c) => pedidos[c] !== undefined && pedidos[c] !== hoy[c]).map((c) => ({
    campo: c,
    de: hoy[c] || '(vacío)',
    a: pedidos[c] as string,
  }));
}

export interface DependenciasDeCambiar {
  productosDeAtlas: () => Promise<ProductoDeAtlas[]>;
  /** Lo que el producto dice HOY en la tienda. */
  datosActuales: (tnProductId: number) => Promise<TnProductDetails | null>;
  /** Guarda. Recibe la ficha COMPLETA porque la tienda pisa todo, no parchea. */
  guardar: (args: { tnProductId: number; ficha: TnProductDetails }) => Promise<boolean>;
}

export async function resolverCambiarDatos(
  input: unknown,
  deps: DependenciasDeCambiar,
  ctx: { turno: number | null; ultimoMensaje?: string },
): Promise<{ __texto: string }> {
  const texto = (t: string) => ({ __texto: t });
  const pedido = (input ?? {}) as { producto?: unknown; confirmado?: unknown };
  const nombre = typeof pedido.producto === 'string' ? pedido.producto.trim() : '';
  if (!nombre) return texto('Falta decir a qué producto. Preguntale cuál.');

  const pedidos = cambiosPedidos(input);
  if (Object.keys(pedidos).length === 0) {
    return texto('No pediste ningún cambio. Preguntale qué quiere cambiar: precio, stock, SKU, nombre o descripción.');
  }

  const eleccion = elegirProductoDeAtlas(nombre, await deps.productosDeAtlas());
  if (eleccion.tipo === 'varios') {
    return texto(mensajeDeVarios(nombre, eleccion.opciones));
  }
  if (eleccion.tipo === 'ninguno') {
    return texto(`No encontré "${nombre}" entre los productos de Atlas. Preguntale a cuál se refiere.`);
  }
  const producto = eleccion.producto;
  if (producto.tnProductId === null) {
    return texto(
      `"${producto.nombre}" no está vinculado a ningún producto de la tienda, así que no sé cuál tocar. Se resuelve la primera vez que le subís una foto con subir_fotos_a_la_tienda.`,
    );
  }

  const actual = await deps.datosActuales(producto.tnProductId);
  if (!actual) {
    return texto('No se pudo leer cómo está ese producto en la tienda. No toqué nada; ofrecé reintentar.');
  }

  // Con varias variantes, precio/stock/SKU son de cada una: no hay uno solo que tocar.
  if (actual.variante_count > 1) {
    const deVariante = CAMPOS_DE_VARIANTE.filter((c) => pedidos[c] !== undefined);
    if (deVariante.length > 0) {
      return texto(
        `No toqué nada: "${producto.nombre}" tiene ${actual.variante_count} variantes en la tienda, así que ${deVariante.join(' y ')} se editan por variante y no sé cuál quiere. Decíle que eso lo cambie desde Tiendanube. El nombre y la descripción sí los puedo cambiar.`,
      );
    }
  }

  const aplican = cambiosQueAplican(pedidos, actual);
  if (aplican.length === 0) {
    return texto(`En la tienda ya dice eso: no hay nada que cambiar en "${producto.nombre}". Decíselo.`);
  }

  const detalle = aplican.map((c) => `${c.campo}: ${c.de} → ${c.a}`).join(', ');
  const clave = `${producto.id}:${aplican.map((c) => `${c.campo}=${c.a}`).join('|')}`;
  const confirmado = pedido.confirmado === true;
  if (
    !puedeActuar({
      accion: NOMBRE_CAMBIAR_DATOS,
      clave,
      confirmado,
      turno: ctx.turno,
      ultimoMensaje: ctx.ultimoMensaje,
    })
  ) {
    anotarAviso(NOMBRE_CAMBIAR_DATOS, clave, ctx.turno);
    return texto(
      `NO TOQUÉ NADA. En "${producto.nombre}" de la tienda quedaría así — ${detalle}. Lo ven sus clientes apenas se guarda y no se deshace desde Atlas. Mostráselo tal cual y preguntale si lo guarda. Si en su próximo mensaje dice que sí, volvé a llamarme con confirmado en true.`,
    );
  }

  const ficha: TnProductDetails = {
    ...actual,
    ...(pedidos.nombre !== undefined ? { nombre: pedidos.nombre } : {}),
    ...(pedidos.descripcion !== undefined ? { descripcion: pedidos.descripcion } : {}),
    ...(pedidos.precio !== undefined ? { precio: pedidos.precio } : {}),
    ...(pedidos.stock !== undefined ? { stock: Number(pedidos.stock) } : {}),
    ...(pedidos.sku !== undefined ? { sku: pedidos.sku } : {}),
  };
  const ok = await deps.guardar({ tnProductId: producto.tnProductId, ficha });
  consumirAviso(NOMBRE_CAMBIAR_DATOS);
  if (!ok) {
    return texto('No se pudo guardar en la tienda. Decíselo sin inventar la causa; no cambió nada.');
  }
  return texto(`Guardado en "${producto.nombre}" de la tienda: ${detalle}. Ya lo ven sus clientes.`);
}
